import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';

// ==================== TYPES ====================
export interface UnmarkedClass {
  class_id: string;
  class_name: string;
  section: string;
  status?: 'marked' | 'unmarked' | 'partially_marked';
  total_students: number;
  attendance_count: number;
  present?: number;
  absent?: number;
  leave?: number;
  half_day?: number;
  skipped_students: number;
}

interface Segment {
  key: 'present' | 'absent' | 'leave' | 'skipped';
  label: string;
  value: number;
  idleGradient: string;
  hoverGradient: string;
  idleShadow: string;
  hoverShadow: string;
  textColor: string;
}

interface DayOption {
  iso: string;
  dayLabel: string;
  dateNum: string;
  isToday: boolean;
}

const buildLastSevenDays = (): DayOption[] => {
  const days: DayOption[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);

    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2);
    const dateNum = String(d.getDate()).padStart(2, '0');

    days.push({ iso, dayLabel, dateNum, isToday: i === 0 });
  }
  return days;
};

const formatFullDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const UnmarkedClassesChart: React.FC<{
  classes: UnmarkedClass[];
  selectedDate?: string;
  onDateChange?: (date: string) => void;
}> = ({ classes, selectedDate, onDateChange }) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [animated, setAnimated] = useState(false);
  const [days] = useState<DayOption[]>(() => buildLastSevenDays());
  const [internalDate, setInternalDate] = useState<string>(
    selectedDate ?? days[days.length - 1].iso
  );

  useEffect(() => {
    if (selectedDate && selectedDate !== internalDate) {
      setInternalDate(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleDayClick = (iso: string) => {
    setInternalDate(iso);
    onDateChange?.(iso);
  };

  const activeDate = selectedDate ?? internalDate;

  if (classes.length === 0) return null;

  const unmarkedCount = classes.filter((c) => c.skipped_students > 0).length;

  const CHART_HEIGHT = 130;
  const MIN_SEGMENT = 3;
  const BAR_WIDTH = 26;
  const maxTotal = Math.max(...classes.map((c) => c.total_students), 1);

  const longestLabelLen = Math.max(
    ...classes.map((c) => `${c.class_name}${c.section ? '-' + c.section : ''}`.length),
    6
  );
  const LABEL_HEIGHT = Math.min(120, Math.max(60, longestLabelLen * 6.5));

  const buildSegments = (c: UnmarkedClass): Segment[] => {
    const leaveTotal = (c.leave ?? 0) + (c.half_day ?? 0);
    const present = c.present ?? Math.max(c.attendance_count - (c.absent ?? 0) - leaveTotal, 0);
    const absent = c.absent ?? 0;

    return [
      {
        key: 'present',
        label: 'Present',
        value: present,
        idleGradient: 'linear-gradient(180deg, #bbf7d0 0%, #4ade80 100%)',
        hoverGradient: 'linear-gradient(180deg, #4ade80 0%, #15803d 100%)',
        idleShadow: '0 3px 8px rgba(34, 197, 94, 0.2)',
        hoverShadow: '0 10px 18px rgba(21, 128, 61, 0.4)',
        textColor: '#16a34a',
      },
      {
        key: 'absent',
        label: 'Absent',
        value: absent,
        idleGradient: 'linear-gradient(180deg, #fecaca 0%, #f87171 100%)',
        hoverGradient: 'linear-gradient(180deg, #f87171 0%, #b91c1c 100%)',
        idleShadow: '0 3px 8px rgba(239, 68, 68, 0.18)',
        hoverShadow: '0 10px 18px rgba(185, 28, 28, 0.4)',
        textColor: '#dc2626',
      },
      {
        key: 'leave',
        label: 'Leave/Half-day',
        value: leaveTotal,
        idleGradient: 'linear-gradient(180deg, #fde68a 0%, #f59e0b 100%)',
        hoverGradient: 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)',
        idleShadow: '0 3px 8px rgba(245, 158, 11, 0.2)',
        hoverShadow: '0 10px 18px rgba(180, 83, 9, 0.4)',
        textColor: '#b45309',
      },
      {
        key: 'skipped',
        label: 'Skipped',
        value: c.skipped_students,
        idleGradient: 'linear-gradient(180deg, #e2e8f0 0%, #94a3b8 100%)',
        hoverGradient: 'linear-gradient(180deg, #94a3b8 0%, #475569 100%)',
        idleShadow: '0 3px 8px rgba(100, 116, 139, 0.18)',
        hoverShadow: '0 10px 18px rgba(71, 85, 105, 0.4)',
        textColor: '#64748b',
      },
    ];
  };

  return (
    <Card
      style={{
        borderRadius: '16px',
        marginBottom: '24px',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* ── header + legend, then day picker, all in one flex row ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '20px',
          marginBottom: '16px',
          justifyContent: 'space-between',
        }}
      >
        {/* header + legend, stacked in their own column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ color: 'white', fontSize: '0.9rem' }}></i>
            </div>
            <h6 style={{ color: '#1e40af', fontWeight: 700, margin: 0, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
              {unmarkedCount} class{unmarkedCount !== 1 ? 'es' : ''} not marked yet
            </h6>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { color: 'linear-gradient(180deg, #4ade80 0%, #16a34a 100%)', label: 'Present' },
              { color: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)', label: 'Absent' },
              { color: 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)', label: 'Leave/Half-day' },
              { color: 'linear-gradient(180deg, #94a3b8 0%, #475569 100%)', label: 'Skipped' },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* segmented day picker, reduced 40% and aligned to the right */}
        <div style={{ flex: '0 0 250px', minWidth: 203 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <span style={{ fontSize: '0.53rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.39px' }}>
              Last 7 days
            </span>
            <span style={{ fontSize: '0.91rem', fontWeight: 700, color: '#1e40af' }}>
              {formatFullDate(activeDate)}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              width: '100%',
              background: '#f1f5f9',
              borderRadius: '8px',
              padding: '3px',
              border: '1px solid #e2e8f0',
              gap: '1.3px',
            }}
          >
            {days.map((d) => {
              const isActive = d.iso === activeDate;
              return (
                <button
                  key={d.iso}
                  onClick={() => handleDayClick(d.iso)}
                  style={{
                    flex: '1 1 0%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1.3px',
                    padding: '5px 1.3px',
                    borderRadius: '5px',
                    border: 'none',
                    background: isActive
                      ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                      : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: isActive ? '0 3px 8px rgba(59, 130, 246, 0.35)' : 'none',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.47rem',
                      fontWeight: 700,
                      color: isActive ? 'rgba(255,255,255,0.85)' : '#94a3b8',
                      textTransform: 'uppercase',
                    }}
                  >
                    {d.dayLabel}
                  </span>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: isActive ? '#ffffff' : '#1e293b',
                    }}
                  >
                    {d.dateNum}
                  </span>
                  {d.isToday && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: isActive ? '#ffffff' : '#22c55e',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bars container.
          NOTE on the fix below: this row scrolls horizontally
          (overflow-x: auto) and previously used justifyContent: 'center'.
          Centering a flex container whose content is WIDER than it is
          means the browser distributes the overflow equally on both
          sides — but scroll position starts at 0, and most browsers won't
          let you scroll into that left-side overflow at all. That's why
          the first class (or two) looked "cut off" and were unreachable
          no matter how far you scrolled. `justifyContent: 'safe center'`
          keeps items centered only while they fit; once they overflow it
          falls back to start-alignment so every item is scrollable into
          view. */}
      <style>{`
        .unmarked-classes-scroll {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 #dbeafe;
        }
        .unmarked-classes-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .unmarked-classes-scroll::-webkit-scrollbar-track {
          background: #dbeafe;
          border-radius: 999px;
        }
        .unmarked-classes-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
          border-radius: 999px;
          border: 1px solid #dbeafe;
        }
        .unmarked-classes-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #3b82f6 0%, #1e40af 100%);
        }
      `}</style>
      <div
        className="unmarked-classes-scroll"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '2.5px',
          overflowX: 'auto',
          overflowY: 'visible',
          paddingTop: '20px',
          paddingLeft: '25px',
          paddingRight: '25px',
          paddingBottom: '22px',
          justifyContent: 'safe center',
        }}
      >
        {classes.map((c, index) => {
          const key = `${c.class_id}-${c.section}`;
          const isHovered = hoveredKey === key;
          const label = `${c.class_name}${c.section ? '-' + c.section : ''}`;
          const total = Math.max(c.total_students, 1);
          const barCapacity = (total / maxTotal) * CHART_HEIGHT;

          const segments = buildSegments(c);
          const finalHeights = segments.map((s) =>
            Math.max(s.value > 0 ? MIN_SEGMENT : 0, (s.value / total) * barCapacity)
          );
          const heights = finalHeights.map((h) => (animated ? h : 0));

          const isEdgeStart = index < 2;
          const isEdgeEnd = index > classes.length - 3;
          const tooltipPosition: React.CSSProperties = isEdgeStart
            ? { left: 0, transform: 'translateX(0)' }
            : isEdgeEnd
            ? { left: 'auto', right: 0, transform: 'translateX(0)' }
            : { left: '50%', transform: 'translateX(-50%)' };

          const totalFinalHeight = finalHeights.reduce((a, b) => a + b, 0);

          return (
            <div
              key={key}
              style={{
                position: 'relative',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: BAR_WIDTH + 12,
              }}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {isHovered && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: `calc(${totalFinalHeight}px + 16px)`,
                    background: '#ffffff',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
                    border: '1px solid #e2e8f0',
                    whiteSpace: 'nowrap',
                    zIndex: 5,
                    ...tooltipPosition,
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                    {label}
                  </div>
                  {segments.map((s) =>
                    s.value > 0 || s.key !== 'leave' ? (
                      <div key={s.key} style={{ fontSize: '0.68rem', fontWeight: 600, color: s.textColor }}>
                        {s.label}: {s.value}
                      </div>
                    ) : null
                  )}
                  <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>
                    Total: {c.total_students}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  height: CHART_HEIGHT,
                  width: BAR_WIDTH,
                  transform: isHovered ? 'scale(1.08)' : 'scale(1)',
                  transformOrigin: 'bottom center',
                  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {(() => {
                  const renderOrder = [...segments].reverse();
                  const renderHeights = renderOrder.map((_, sIndex) => heights[segments.length - 1 - sIndex]);
                  const renderFinalHeights = renderOrder.map((_, sIndex) => finalHeights[segments.length - 1 - sIndex]);
                  const topVisibleIdx = renderFinalHeights.findIndex((h) => h > 0);
                  let bottomVisibleIdx = -1;
                  for (let i = renderFinalHeights.length - 1; i >= 0; i--) {
                    if (renderFinalHeights[i] > 0) {
                      bottomVisibleIdx = i;
                      break;
                    }
                  }

                  return renderOrder.map((s, sIndex) => {
                    const h = renderHeights[sIndex];
                    const isTopVisible = sIndex === topVisibleIdx;
                    const isBottomVisible = sIndex === bottomVisibleIdx;
                    const showDivider = h > 0 && !isBottomVisible;
                    return (
                      <div
                        key={s.key}
                        style={{
                          width: '100%',
                          height: `${h}px`,
                          background: isHovered ? s.hoverGradient : s.idleGradient,
                          borderRadius: h > 0
                            ? `${isTopVisible ? '8px 8px' : '0 0'} ${isBottomVisible ? '6px 6px' : '0 0'}`
                            : 0,
                          borderBottom: showDivider ? '2px solid #ffffff' : 'none',
                          boxShadow: h > 0 ? (isHovered ? s.hoverShadow : s.idleShadow) : 'none',
                          transition: `height 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.03 + sIndex * 0.03}s, background 0.25s ease, box-shadow 0.25s ease`,
                        }}
                      />
                    );
                  });
                })()}
              </div>

              <div
                style={{
                  marginTop: '8px',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: isHovered ? '#dc2626' : '#64748b',
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  height: `${LABEL_HEIGHT}px`,
                  width: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  transition: 'color 0.2s ease',
                }}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default UnmarkedClassesChart;